<template>
  <div class="marketing-page">
    <div class="site-banner">
      <a href="https://youtu.be/_otlqHMRo7k" target="_blank" rel="noopener noreferrer">
        <span>Partner education</span> Watch how two pet retailers use UCARI to boost sales
        <span aria-hidden="true">→</span>
      </a>
    </div>

    <MarketingHeader />

    <main id="main">
      <section class="partner-hero">
        <div class="portal-container partner-hero__content">
          <div class="hero-badge"><span uk-icon="icon: check"></span> Trusted by thousands of retail partners</div>
          <h1>Helping businesses build healthier customers and stronger relationships</h1>
          <p>Whether you serve pets, people, or both, UCARI combines wellness testing with digital engagement tools that support loyalty, repeat visits, and long-term growth.</p>
          <div class="partner-hero__actions">
            <router-link :to="{ name: 'RegisterUser' }" class="uk-button uk-button-primary uk-button-large">Become a retail partner</router-link>
            <router-link :to="{ name: 'LoginUser' }" class="uk-button uk-button-default uk-button-large">Sign in to the portal</router-link>
          </div>
          <div class="partner-hero__trust">
            <span><strong>80,000+</strong> tests delivered</span>
            <span><strong>Up to 50%</strong> retail margins</span>
            <span><strong>No MOQ</strong> to get started</span>
          </div>
        </div>
      </section>

      <section class="brand-proof">
        <div class="portal-container brand-proof__inner">
          <p>Built for independent retailers, wellness professionals, and pet stores</p>
          <div><span>Simple setup</span><span>Fast fulfillment</span><span>Ongoing partner support</span></div>
        </div>
      </section>

      <section class="marketing-section marketing-section--white">
        <div class="portal-container">
          <div class="marketing-section__heading">
            <span class="eyebrow">A complete category</span>
            <h2>Products customers understand and staff can recommend</h2>
            <p>A small-footprint category that creates new sales opportunities across pets, human wellness, and digital engagement.</p>
          </div>

          <div class="marketing-card-grid">
            <article class="marketing-card product-story-card product-story-card--pet">
              <div class="product-story-card__image"><img src="/images/pet-kit-box.png" alt="UCARI Pet Intolerance Test Kit" /></div>
              <div class="product-story-card__body">
                <span class="product-pill product-pill--green">For pets</span>
                <h3>Pet Intolerance Kit</h3>
                <p>Helps pet parents identify food and environmental items that may be contributing to common symptoms.</p>
                <router-link to="/pet-kit">Explore the pet kit <span>→</span></router-link>
              </div>
            </article>

            <article class="marketing-card product-story-card product-story-card--human">
              <div class="product-story-card__image"><img src="/images/human-kit-box.png" alt="UCARI Human Intolerance Test Kit" /></div>
              <div class="product-story-card__body">
                <span class="product-pill product-pill--pink">For people</span>
                <h3>Human Intolerance Kit</h3>
                <p>At-home testing that gives customers a clearer starting point for personalized food and lifestyle choices.</p>
                <router-link to="/human-kit">Explore the human kit <span>→</span></router-link>
              </div>
            </article>

            <article class="marketing-card product-story-card product-story-card--digital">
              <div class="product-story-card__icon" uk-icon="icon: search; ratio: 2"></div>
              <div class="product-story-card__body">
                <span class="product-pill">Digital companion</span>
                <h3>Ingredient Checker</h3>
                <p>Customers can scan and compare product ingredients against their personal results while they shop.</p>
                <router-link to="/ingredient-checker">See the tool <span>→</span></router-link>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section class="marketing-section marketing-section--blue">
        <div class="portal-container">
          <div class="marketing-section__heading">
            <span class="eyebrow">Made for retail</span>
            <h2>Everything you need to launch and grow</h2>
            <p>From your first order to every reorder, UCARI makes it easy to add, promote, and grow a profitable wellness category.</p>
          </div>
          <div class="benefit-grid">
            <article v-for="benefit in benefits" :key="benefit.title" class="benefit-card">
              <span :uk-icon="`icon: ${benefit.icon}; ratio: 1.25`"></span>
              <h3>{{ benefit.title }}</h3>
              <p>{{ benefit.text }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="marketing-section marketing-section--white">
        <div class="portal-container launch-layout">
          <div>
            <span class="eyebrow">Your launch plan</span>
            <h2>From approval to your first sale in four simple steps</h2>
            <p class="launch-layout__lead">The portal keeps wholesale ordering simple while our team helps your staff feel confident introducing UCARI.</p>
            <router-link :to="{ name: 'RegisterUser' }" class="uk-button uk-button-primary">Apply for partnership</router-link>
          </div>
          <ol class="launch-steps">
            <li v-for="(step, index) in steps" :key="step.title">
              <span>{{ index + 1 }}</span>
              <div><h3>{{ step.title }}</h3><p>{{ step.text }}</p></div>
            </li>
          </ol>
        </div>
      </section>

      <section class="marketing-section testimonials-section">
        <div class="portal-container">
          <div class="marketing-section__heading"><span class="eyebrow">Partner stories</span><h2>Trusted on the sales floor</h2></div>
          <div class="testimonial-grid">
            <figure v-for="quote in testimonials" :key="quote.author" class="testimonial-card">
              <h3>{{ quote.title }}</h3>
              <blockquote>“{{ quote.text }}”</blockquote><figcaption>{{ quote.author }}</figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section class="marketing-section marketing-section--white">
        <div class="portal-container faq-layout">
          <div><span class="eyebrow">Common questions</span><h2>Partner program FAQ</h2><p>Everything you need to know to get started.</p></div>
          <ul uk-accordion="multiple: false" class="portal-accordion">
            <li v-for="faq in faqs" :key="faq.question">
              <a class="uk-accordion-title" href>{{ faq.question }}</a>
              <div class="uk-accordion-content"><p>{{ faq.answer }}</p></div>
            </li>
          </ul>
        </div>
      </section>

      <section class="portal-cta">
        <div class="portal-container">
          <div><span>Ready to add UCARI?</span><h2>Build a more valuable wellness category.</h2><p>Apply today. There is no minimum opening order and no credit card is required to create an account.</p></div>
          <div class="portal-cta__actions"><router-link :to="{ name: 'RegisterUser' }" class="uk-button uk-button-primary uk-button-large">Become a partner</router-link><router-link :to="{ name: 'LoginUser' }" class="uk-button uk-button-default uk-button-large">Partner sign in</router-link></div>
        </div>
      </section>
    </main>

    <MarketingFooter />
    <PublicFloatingCtas />
  </div>
</template>

<script>
import MarketingHeader from './components/MarketingHeader.vue';
import MarketingFooter from './components/MarketingFooter.vue';
import PublicFloatingCtas from './components/PublicFloatingCtas.vue';

export default {
  name: 'HomePage',
  components: { MarketingHeader, MarketingFooter, PublicFloatingCtas },
  data() {
    return {
      benefits: [
        { icon: 'tag', title: 'Healthy wholesale margins', text: 'Offer a premium wellness product with partner pricing designed for sustainable retail growth.' },
        { icon: 'users', title: 'Staff training', text: 'Give your team clear language, product education, and tools that make recommendations feel natural.' },
        { icon: 'image', title: 'Ready-to-use marketing', text: 'Launch with signage, digital assets, FAQs, and merchandising guidance already prepared.' },
        { icon: 'receiver', title: 'Responsive support', text: 'Get help from a partner team that understands both the products and the realities of retail.' },
      ],
      steps: [
        { title: 'Create your account', text: 'Tell us about your business and submit your partner application.' },
        { title: 'Get approved', text: 'Our team reviews your information and activates wholesale access.' },
        { title: 'Place your first order', text: 'Choose live products and pack sizes in the secure partner shop.' },
        { title: 'Launch with confidence', text: 'Use the included training and marketing resources to begin selling.' },
      ],
      testimonials: [
        { title: 'Pet Supply Store', text: 'This has been one of the best products ever brought to us. It has helped so many pet owners.', author: 'Lucy Creek Pets' },
        { title: 'Health Food Store', text: 'UCARI is a great tool for me. The results help us have a much more useful conversation with every customer.', author: "Sean O'Hare, Suncoast Food Co-op" },
        { title: 'Health Care Provider', text: 'It bridges the gap between symptoms and daily habits in a way clients can understand and act on.', author: 'Lynn Morales, ND' },
      ],
      faqs: [
        { question: 'Is there a minimum opening order?', answer: 'No. You can start with the quantity that is right for your store and reorder through the partner portal at any time.' },
        { question: 'What marketing support is included?', answer: 'Partners receive product education, printable materials, digital assets, customer FAQs, and merchandising guidance.' },
        { question: 'How does wholesale pricing work?', answer: 'Approved partners unlock wholesale pack pricing in the portal. Live product and variation pricing is shown before anything is added to the cart.' },
        { question: 'How do customers receive results?', answer: 'Customers register their kit and receive results digitally after their sample is received and processed.' },
      ],
    };
  },
};
</script>
